import { useAppSelector } from 'hooks/useAppSelector';
import { useTranslation } from 'hooks/useTranslation';
import type { WorkspaceType } from 'state/workspaces/model';

import { DocumentLink } from '../design-system/DocumentLink';
import { LanguageLink } from '../design-system/LanguageLink';
import {
  getTermsheetDocumentError,
  getTermsheetDocuments,
  getTermsheetDocumentTimeout,
  hasTermsheetError,
} from '../selectors';
import { useOnDownloadDocument } from './useOnDownloadDocument';

export function TermsheetDocuments({ tabId, workspaceType }: { tabId: string; workspaceType: WorkspaceType }) {
  const label = useTranslation('form.buttons.TermsheetButton');
  const documents = useAppSelector(state => getTermsheetDocuments(state, workspaceType, tabId));
  const rejections = useAppSelector(state => getTermsheetDocumentError(state, workspaceType, tabId));
  const hasError = useAppSelector(state => hasTermsheetError(state, workspaceType, tabId));
  const hasTimeout = useAppSelector(state => getTermsheetDocumentTimeout(state, workspaceType, tabId));
  const { onDownload, isDownloading, onShowError } = useOnDownloadDocument({
    errorLabelKey: 'downloadTermsheet.functional.error',
    event: 'Download termsheet',
  });

  if (hasTimeout || hasError) {
    return (
      <DocumentLink label={label}>
        <LanguageLink
          langCode={null}
          status="error"
          severity={hasTimeout ? 'warning' : 'danger'}
          onShowError={hasTimeout ? () => onShowError([]) : () => onShowError(rejections ?? [])}
        />
      </DocumentLink>
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <DocumentLink label={label}>
      {documents.map((doc, i) => {
        if (doc.documentToken !== undefined && isDownloading) {
          return <LanguageLink key={i} langCode={doc.language} status="pending" />;
        }
        return <LanguageLink key={i} langCode={doc.language} status="success" onDownload={() => onDownload(doc)} />;
      })}
    </DocumentLink>
  );
}
